/**
 * RecipeFoodPickerDialog — search a food, pick a serving + qty, return the
 * payload via onAdd. Mirrors the consultation AddFoodDialog UI but is
 * appointment-agnostic so it can be reused by the recipe builder.
 */
import * as React from "react";
import { Apple, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { SELECT_CONTENT_MAX_H } from "@/components/settings/recipe-select-constants";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
} from "@/components/consultation/shared/Widget";
import { FoodNutrientPieChart } from "@/components/consultation/tabs/nutrition/components/nutrition-charts";
import { NUTRITION_COLORS } from "@/components/consultation/tabs/nutrition/components/nutrition-chart-config";
import { quantityPatternFor } from "@/components/consultation/tabs/nutrition/components/meal-constants";
import {
  useFoodSearch,
  useFoodServings,
  useFoodTypes,
} from "@/hooks/useNutritionEvaluation";
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";

export interface PickedFoodPayload {
  foodId: number;
  foodName: string;
  servingId: number;
  servingLabel: string;
  servingMultiplier: number;
  // Gram weight of one serving unit (e.g. "Cup (~240g)" -> 240). Used to
  // send ingredient serving context to the backend.
  servingWeightG: number;
  requiresWholeServings: boolean;
  quantity: number;
}

interface RecipeFoodPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (payload: PickedFoodPayload) => void | Promise<void>;
}

interface SearchFoodItem {
  id: number;
  name: string;
  source?: string;
  food_type?: string;
  requires_whole_servings?: boolean;
  energy_kcal?: string;
  food_item_nutrient_values?: {
    energy: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
}

const NUTRIENT_LEGEND_ITEMS = [
  {
    key: "protein",
    label: "Protein",
    kcalPerGram: 4,
    color: NUTRITION_COLORS.protein,
  },
  {
    key: "carbs",
    label: "Carbs",
    kcalPerGram: 4,
    color: NUTRITION_COLORS.carbs,
  },
  { key: "fat", label: "Fat", kcalPerGram: 9, color: NUTRITION_COLORS.fats },
  {
    key: "fiber",
    label: "Fiber",
    kcalPerGram: 2,
    color: NUTRITION_COLORS.fiber,
  },
] as const;

export const RecipeFoodPickerDialog: React.FC<RecipeFoodPickerDialogProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedFood, setSelectedFood] = React.useState<SearchFoodItem | null>(
    null
  );
  const [selectedServing, setSelectedServing] = React.useState<string>("");
  const [quantity, setQuantity] = React.useState("1");
  const [selectedFoodType, setSelectedFoodType] = React.useState("all");
  const [sourceFilter, setSourceFilter] = React.useState("all");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // A lone "." (or empty) is a valid in-progress keystroke but not a number;
  // treat it as invalid so it can never reach the wire as "NaN".
  const parsedQuantity = parseFloat(quantity);
  const isQuantityValid = Number.isFinite(parsedQuantity) && parsedQuantity > 0;

  // Supplements ARE valid recipe ingredients — a protein shake is milk, whey
  // and sometimes a capsule. D-5 was withdrawn once the resolver was taught to
  // scale by the food's basis instead of by weight.
  const { data: foodTypes = [] } = useFoodTypes();
  const foodTypeTabs = React.useMemo(
    () => [{ value: "all", label: "All" }, ...foodTypes],
    [foodTypes]
  );

  const handleFoodTypeChange = React.useCallback((value: string) => {
    setSelectedFoodType(value);
    setSelectedFood(null);
    setSelectedServing("");
    setQuantity("1");
  }, []);

  // Source is filtered server-side (?source=…) so a chip returns the full
  // catalog for that source, not just the sources present in the current page.
  const { data: searchResults, isLoading: isSearching } = useFoodSearch(
    searchQuery,
    undefined,
    searchQuery.length >= 2,
    selectedFoodType === "all" ? undefined : selectedFoodType,
    sourceFilter === "all" ? undefined : sourceFilter
  );

  // Canonical source list from tag-metadata, not derived from the current
  // results page.
  const { data: tagMetadata } = useTagMetadata();
  const sourceChips = React.useMemo(
    () =>
      (tagMetadata?.sources_and_brands.food_sources ?? [])
        .map((s) => s.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [tagMetadata]
  );

  const filteredResults = (searchResults?.results ?? []) as SearchFoodItem[];

  const { data: servings = [] } = useFoodServings(
    selectedFood?.id || 0,
    !!selectedFood
  );

  const resetForm = React.useCallback(() => {
    setSearchQuery("");
    setSelectedFood(null);
    setSelectedServing("");
    setQuantity("1");
    setSourceFilter("all");
    setSelectedFoodType("all");
  }, []);

  // Whole servings only when the food contains something indivisible. The 400
  // is the backstop; this stops the fraction being typed in the first place.
  const wholeServingsOnly = Boolean(selectedFood?.requires_whole_servings);

  const handleQuantityChange = (value: string) => {
    if (!quantityPatternFor(wholeServingsOnly).test(value)) return;
    setQuantity(value);
  };

  const handleAdd = async () => {
    if (!selectedFood || !selectedServing || !isQuantityValid || isSubmitting) {
      return;
    }
    const serving = servings.find((s) => s.id.toString() === selectedServing);
    // Guard the pairing invariant client-side: the serving must belong to the
    // selected food (brief §4 — else the backend 400s).
    if (!serving || serving.food_item_id !== selectedFood.id) return;

    const servingLabel =
      serving.description || serving.serving_unit?.name || "Serving";

    setIsSubmitting(true);
    try {
      await onAdd({
        foodId: selectedFood.id,
        foodName: selectedFood.name,
        servingId: serving.id,
        servingLabel,
        servingMultiplier: parseFloat(serving.multiplier) || 1,
        servingWeightG: parseFloat(serving.weight_g) || 0,
        requiresWholeServings: wholeServingsOnly,
        quantity: parsedQuantity,
      });
    } catch {
      return;
    } finally {
      setIsSubmitting(false);
    }
    onClose();
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        resetForm();
      }}
    >
      <DialogContent className="sm:max-w-[900px] max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <Apple className="h-5 w-5 text-primary" />
            Add Ingredient
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="min-h-0 flex-1 overflow-y-auto p-6 bg-background flex flex-col gap-4">
          <Input
            placeholder="Start typing food item..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedFood(null);
            }}
            className="w-full"
          />

          <p className="z-caption text-muted-foreground -mt-2">
            {isSearching
              ? "Searching…"
              : searchQuery.length < 2
                ? "Type at least 2 characters to search"
                : `${filteredResults.length} items found`}
          </p>

          <Tabs
            value={selectedFoodType}
            onValueChange={handleFoodTypeChange}
            className="w-full"
          >
            <TabsList className="w-full">
              {foodTypeTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {sourceChips.length > 0 && (
            <ToggleGroup
              type="single"
              spacing={2}
              value={sourceFilter}
              onValueChange={(v) => v && setSourceFilter(v)}
              variant="outline"
              size="sm"
              className="flex-wrap"
            >
              <ToggleGroupItem value="all">All</ToggleGroupItem>
              {sourceChips.map((source) => (
                <ToggleGroupItem key={source} value={source}>
                  {source}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}

          <Widget className="overflow-hidden">
            <div className="h-64 overflow-y-auto">
              {isSearching && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}

              {filteredResults.length > 0 && (
                <table className="w-full caption-bottom text-sm">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="px-4 text-muted-foreground/80 sticky top-0 z-10 bg-muted">
                        Food Name
                      </TableHead>
                      <TableHead className="px-4 w-40 text-muted-foreground/80 sticky top-0 z-10 bg-muted">
                        Source
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResults.map((food) => (
                      <TableRow
                        key={food.id}
                        data-state={
                          selectedFood?.id === food.id ? "selected" : undefined
                        }
                        className="cursor-pointer data-[state=selected]:bg-primary/10 hover:bg-muted/40"
                        onClick={() => {
                          setSelectedFood(food);
                          setSelectedServing("");
                          setQuantity("1");
                        }}
                      >
                        <TableCell className="px-4 z-body-md text-foreground">
                          {food.name}
                        </TableCell>
                        <TableCell className="px-4 text-muted-foreground">
                          {food.source && food.source.trim() ? (
                            <Badge variant="secondary">{food.source}</Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </table>
              )}

              {searchQuery.length >= 2 &&
                !isSearching &&
                filteredResults.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No food items found
                  </div>
                )}

              {searchQuery.length < 2 && (
                <div className="text-center py-8 text-muted-foreground">
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          </Widget>

          {selectedFood && (
            <Widget>
              <WidgetHeader
                title={selectedFood.name}
                className="justify-center bg-muted/40 border-b border-border/60 py-2"
              />
              <WidgetContent className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                <Widget>
                  <WidgetContent className="py-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="z-h3 text-foreground w-28 shrink-0">
                        Serving Size
                      </label>
                      <Select
                        value={selectedServing}
                        onValueChange={setSelectedServing}
                        disabled={servings.length === 0}
                      >
                        <SelectTrigger className="flex-1 min-w-0">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className={SELECT_CONTENT_MAX_H}
                        >
                          {servings.map((serving) => (
                            <SelectItem
                              key={serving.id}
                              value={serving.id.toString()}
                            >
                              {serving.description ||
                                serving.serving_unit?.name ||
                                "Serving"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="z-h3 text-foreground w-28 shrink-0">
                        Quantity
                      </label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        aria-label="Quantity"
                        placeholder="-"
                        value={quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        disabled={!selectedServing}
                        step={wholeServingsOnly ? 1 : undefined}
                        className="w-20 text-center"
                      />
                      {wholeServingsOnly && (
                        <p className="z-caption text-muted-foreground">
                          Whole servings only — this food contains something
                          that can't be halved.
                        </p>
                      )}
                    </div>
                  </WidgetContent>
                </Widget>

                <Widget>
                  <WidgetContent className="py-4 h-full flex items-center justify-center">
                    {selectedFood.food_item_nutrient_values ? (
                      <div className="flex items-center justify-center gap-4">
                        <FoodNutrientPieChart
                          energy={selectedFood.food_item_nutrient_values.energy}
                          protein={
                            selectedFood.food_item_nutrient_values.protein
                          }
                          carbs={selectedFood.food_item_nutrient_values.carbs}
                          fat={selectedFood.food_item_nutrient_values.fat}
                          fiber={selectedFood.food_item_nutrient_values.fiber}
                        />
                        <div className="flex min-w-32 flex-col gap-1.5">
                          {NUTRIENT_LEGEND_ITEMS.map((item) => {
                            const nutrientValue =
                              selectedFood.food_item_nutrient_values?.[
                                item.key
                              ] ?? 0;
                            const energy =
                              selectedFood.food_item_nutrient_values?.energy ??
                              0;
                            const pct =
                              energy > 0
                                ? (nutrientValue * item.kcalPerGram * 100) /
                                  energy
                                : 0;
                            return (
                              <div
                                key={item.key}
                                className="flex items-center gap-2 z-caption"
                              >
                                <span
                                  className="size-2.5 shrink-0 rounded-full"
                                  style={{ backgroundColor: item.color }}
                                  aria-hidden
                                />
                                <span className="min-w-0 flex-1 text-foreground">
                                  {item.label}
                                </span>
                                <span className="tabular-nums text-muted-foreground">
                                  {nutrientValue.toFixed(1)}g
                                </span>
                                <span className="w-12 text-right tabular-nums text-muted-foreground">
                                  {pct.toFixed(1)}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground z-body py-6">
                        Nutrient preview unavailable
                      </div>
                    )}
                  </WidgetContent>
                </Widget>
              </WidgetContent>
            </Widget>
          )}
        </div>

        <DialogFooter className="px-6 py-4 mt-auto border-t border-border/60 bg-background sm:justify-center">
          <Button
            onClick={handleAdd}
            disabled={
              !selectedFood ||
              !selectedServing ||
              !isQuantityValid ||
              isSubmitting
            }
          >
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            Add Ingredient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecipeFoodPickerDialog;
